import type { ReactNode, SVGProps } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * UI icons exported 1:1 from Figma (component set "Icons" and the library icons used in the design),
 * colour = `currentColor`. Duotone icons keep their 20–30 % opacity layer. Content icons (categories,
 * benefits, stores) are images in the administration, not here.
 */
const ICONS = {
  'arrow-circle-left': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          fill="currentColor"
        />
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M11.43 15.18L8.25 12L11.43 8.82"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.75 12H8.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'arrow-up-right': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M15.63 5V13.13C15.63 13.29 15.56 13.45 15.44 13.57C15.32 13.68 15.17 13.75 15 13.75C14.83 13.75 14.68 13.68 14.56 13.57C14.44 13.45 14.38 13.29 14.38 13.13V6.51L5.44 15.44C5.33 15.56 5.17 15.63 5 15.63C4.83 15.63 4.68 15.56 4.56 15.44C4.44 15.32 4.37 15.17 4.37 15C4.37 14.83 4.44 14.68 4.56 14.56L13.49 5.63H6.88C6.71 5.63 6.55 5.56 6.43 5.44C6.32 5.32 6.25 5.17 6.25 5C6.25 4.83 6.32 4.68 6.43 4.56C6.55 4.44 6.71 4.38 6.88 4.38H15C15.17 4.38 15.32 4.44 15.44 4.56C15.56 4.68 15.63 4.83 15.63 5Z"
          fill="currentColor"
        />
      </>
    ),
  },
  at: {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          opacity="0.3"
          d="M10 17.5C14.14 17.5 17.5 14.14 17.5 10C17.5 5.86 14.14 2.5 10 2.5C5.86 2.5 2.5 5.86 2.5 10C2.5 14.14 5.86 17.5 10 17.5Z"
          fill="currentColor"
        />
        <path
          d="M10 13.12C11.73 13.12 13.12 11.73 13.12 10C13.12 8.27 11.73 6.87 10 6.87C8.27 6.87 6.87 8.27 6.87 10C6.87 11.73 8.27 13.12 10 13.12Z"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.15 16.25C12.75 17.17 11.09 17.61 9.43 17.48C7.76 17.35 6.18 16.67 4.95 15.55C3.71 14.42 2.89 12.92 2.61 11.27C2.32 9.62 2.6 7.93 3.39 6.45C4.18 4.98 5.44 3.82 6.97 3.14C8.5 2.46 10.21 2.32 11.83 2.73C13.45 3.13 14.89 4.07 15.91 5.39C16.94 6.71 17.5 8.33 17.5 10C17.5 11.73 16.87 13.12 15.31 13.12C13.75 13.12 13.12 11.73 13.12 10V6.87"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  bank: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path opacity="0.2" d="M3 9H21L12 3L3 9Z" fill="currentColor" />
        <path
          d="M3 9H21L12 3L3 9Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.25 9V16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.75 9V16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.25 9V16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18.75 9V16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 16.5H21"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.5 19.5H22.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  briefcase: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 14.25C8.84 14.26 5.74 13.42 3 11.84V19.5C3 19.6 3.02 19.7 3.06 19.79C3.1 19.88 3.15 19.96 3.22 20.03C3.29 20.1 3.37 20.16 3.46 20.19C3.55 20.23 3.65 20.25 3.75 20.25H20.25C20.35 20.25 20.45 20.23 20.54 20.19C20.63 20.16 20.71 20.1 20.78 20.03C20.85 19.96 20.91 19.88 20.94 19.79C20.98 19.7 21 19.6 21 19.5V11.84C18.27 13.42 15.16 14.26 12 14.25Z"
          fill="currentColor"
        />
        <path
          d="M20.25 6.75H3.75C3.34 6.75 3 7.09 3 7.5V19.5C3 19.91 3.34 20.25 3.75 20.25H20.25C20.67 20.25 21 19.91 21 19.5V7.5C21 7.09 20.67 6.75 20.25 6.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.75 6.75V5.25C15.75 4.85 15.59 4.47 15.31 4.19C15.03 3.91 14.65 3.75 14.25 3.75H9.75C9.35 3.75 8.97 3.91 8.69 4.19C8.41 4.47 8.25 4.85 8.25 5.25V6.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21 11.84C18.27 13.42 15.16 14.26 12 14.25C8.84 14.26 5.74 13.42 3 11.84"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.88 11.25H13.13"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'caret-down': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M16.69 7.94L10.44 14.19C10.38 14.25 10.32 14.3 10.24 14.33C10.16 14.36 10.08 14.38 10 14.38C9.92 14.38 9.84 14.36 9.76 14.33C9.68 14.3 9.62 14.25 9.56 14.19L3.31 7.94C3.19 7.82 3.12 7.67 3.12 7.5C3.12 7.33 3.19 7.18 3.31 7.06C3.43 6.94 3.58 6.87 3.75 6.87C3.92 6.87 4.08 6.94 4.19 7.06L10 12.87L15.81 7.06C15.87 7 15.93 6.95 16.01 6.92C16.09 6.89 16.17 6.87 16.25 6.87C16.33 6.87 16.41 6.89 16.49 6.92C16.57 6.95 16.63 7 16.69 7.06C16.75 7.12 16.8 7.18 16.83 7.26C16.86 7.34 16.88 7.42 16.88 7.5C16.88 7.58 16.86 7.66 16.83 7.74C16.8 7.82 16.75 7.88 16.69 7.94Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'caret-down-fill': {
    viewBox: '0 0 14 14',
    body: (
      <>
        <path
          d="M11.78 5.08C11.75 5 11.69 4.93 11.62 4.89C11.55 4.84 11.46 4.81 11.38 4.81H2.63C2.54 4.81 2.45 4.84 2.38 4.89C2.31 4.93 2.25 5 2.22 5.08C2.19 5.16 2.18 5.25 2.2 5.34C2.21 5.42 2.25 5.5 2.32 5.56L6.69 9.93C6.73 9.97 6.78 10.01 6.83 10.03C6.89 10.05 6.94 10.06 7 10.06C7.06 10.06 7.11 10.05 7.17 10.03C7.22 10.01 7.27 9.97 7.31 9.93L11.68 5.56C11.75 5.5 11.79 5.42 11.8 5.34C11.82 5.25 11.81 5.16 11.78 5.08Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'caret-left': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M8.47 12.53L15.97 20.03C16.04 20.1 16.12 20.16 16.21 20.19C16.3 20.23 16.4 20.25 16.5 20.25C16.6 20.25 16.7 20.23 16.79 20.19C16.88 20.16 16.96 20.1 17.03 20.03C17.1 19.96 17.16 19.88 17.19 19.79C17.23 19.7 17.25 19.6 17.25 19.5C17.25 19.4 17.23 19.3 17.19 19.21C17.16 19.12 17.1 19.04 17.03 18.97L10.06 12L17.03 5.03C17.17 4.89 17.25 4.7 17.25 4.5C17.25 4.3 17.17 4.11 17.03 3.97C16.89 3.83 16.7 3.75 16.5 3.75C16.3 3.75 16.11 3.83 15.97 3.97L8.47 11.47C8.4 11.54 8.34 11.62 8.31 11.71C8.27 11.8 8.25 11.9 8.25 12C8.25 12.1 8.27 12.2 8.31 12.29C8.34 12.38 8.4 12.46 8.47 12.53Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'caret-right': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M17.03 12.53L9.53 20.03C9.46 20.1 9.38 20.16 9.29 20.19C9.2 20.23 9.1 20.25 9 20.25C8.9 20.25 8.8 20.23 8.71 20.19C8.62 20.16 8.54 20.1 8.47 20.03C8.4 19.96 8.34 19.88 8.31 19.79C8.27 19.7 8.25 19.6 8.25 19.5C8.25 19.4 8.27 19.3 8.31 19.21C8.34 19.12 8.4 19.04 8.47 18.97L15.44 12L8.47 5.03C8.33 4.89 8.25 4.7 8.25 4.5C8.25 4.3 8.33 4.11 8.47 3.97C8.61 3.83 8.8 3.75 9 3.75C9.2 3.75 9.39 3.83 9.53 3.97L17.03 11.47C17.1 11.54 17.16 11.62 17.19 11.71C17.23 11.8 17.25 11.9 17.25 12C17.25 12.1 17.23 12.2 17.19 12.29C17.16 12.38 17.1 12.46 17.03 12.53Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'caret-up': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M16.69 12.94C16.63 13 16.57 13.05 16.49 13.08C16.41 13.11 16.33 13.13 16.25 13.13C16.17 13.13 16.09 13.11 16.01 13.08C15.93 13.05 15.87 13 15.81 12.94L10 7.13L4.19 12.94C4.08 13.06 3.92 13.13 3.75 13.13C3.58 13.13 3.43 13.06 3.31 12.94C3.19 12.82 3.12 12.67 3.12 12.5C3.12 12.33 3.19 12.18 3.31 12.06L9.56 5.81C9.62 5.75 9.68 5.7 9.76 5.67C9.84 5.64 9.92 5.62 10 5.62C10.08 5.62 10.16 5.64 10.24 5.67C10.32 5.7 10.38 5.75 10.44 5.81L16.69 12.06C16.75 12.12 16.8 12.18 16.83 12.26C16.86 12.34 16.88 12.42 16.88 12.5C16.88 12.58 16.86 12.66 16.83 12.74C16.8 12.82 16.75 12.88 16.69 12.94Z"
          fill="currentColor"
        />
      </>
    ),
  },
  cart: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M18 17.25H6.42C6.25 17.25 6.08 17.19 5.94 17.08C5.81 16.96 5.71 16.81 5.68 16.63L3.18 2.87C3.15 2.69 3.06 2.54 2.92 2.42C2.79 2.31 2.62 2.25 2.44 2.25H0.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.75 21C7.79 21 8.63 20.16 8.63 19.13C8.63 18.09 7.79 17.25 6.75 17.25C5.71 17.25 4.88 18.09 4.88 19.13C4.88 20.16 5.71 21 6.75 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 21C19.04 21 19.88 20.16 19.88 19.13C19.88 18.09 19.04 17.25 18 17.25C16.96 17.25 16.13 18.09 16.13 19.13C16.13 20.16 16.96 21 18 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3.75 6H20.1C20.21 6 20.32 6.02 20.42 6.07C20.52 6.12 20.61 6.19 20.68 6.27C20.75 6.35 20.8 6.45 20.83 6.56C20.85 6.67 20.86 6.78 20.84 6.88L19.61 13.63C19.58 13.81 19.49 13.96 19.35 14.08C19.22 14.19 19.05 14.25 18.87 14.25H5.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  check: {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M17.22 5.82L7.62 15.42C7.57 15.48 7.5 15.52 7.43 15.55C7.36 15.58 7.28 15.6 7.2 15.6C7.12 15.6 7.04 15.58 6.97 15.55C6.9 15.52 6.83 15.48 6.78 15.42L2.58 11.22C2.46 11.11 2.4 10.96 2.4 10.8C2.4 10.64 2.46 10.49 2.58 10.38C2.69 10.26 2.84 10.2 3 10.2C3.16 10.2 3.31 10.26 3.42 10.38L7.2 14.15L16.38 4.98C16.49 4.86 16.64 4.8 16.8 4.8C16.96 4.8 17.11 4.86 17.22 4.98C17.34 5.09 17.4 5.24 17.4 5.4C17.4 5.56 17.34 5.71 17.22 5.82Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'check-circle': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          fill="currentColor"
        />
        <path
          d="M16.13 9.75L10.63 15L7.88 12.38"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'chevron-right': {
    viewBox: '0 0 18 18',
    body: (
      <>
        <path
          d="M6.75 3.38L12.38 9L6.75 14.63"
          stroke="currentColor"
          strokeWidth="1.13"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'circles-three': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 10.13C14.07 10.13 15.75 8.45 15.75 6.38C15.75 4.3 14.07 2.63 12 2.63C9.93 2.63 8.25 4.3 8.25 6.38C8.25 8.45 9.93 10.13 12 10.13Z"
          fill="currentColor"
        />
        <path
          opacity="0.2"
          d="M17.63 19.88C19.7 19.88 21.38 18.2 21.38 16.13C21.38 14.05 19.7 12.38 17.63 12.38C15.55 12.38 13.88 14.05 13.88 16.13C13.88 18.2 15.55 19.88 17.63 19.88Z"
          fill="currentColor"
        />
        <path
          opacity="0.2"
          d="M6.38 19.88C8.45 19.88 10.13 18.2 10.13 16.13C10.13 14.05 8.45 12.38 6.38 12.38C4.3 12.38 2.63 14.05 2.63 16.13C2.63 18.2 4.3 19.88 6.38 19.88Z"
          fill="currentColor"
        />
        <path
          d="M12 10.13C14.07 10.13 15.75 8.45 15.75 6.38C15.75 4.3 14.07 2.63 12 2.63C9.93 2.63 8.25 4.3 8.25 6.38C8.25 8.45 9.93 10.13 12 10.13Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.63 19.88C19.7 19.88 21.38 18.2 21.38 16.13C21.38 14.05 19.7 12.38 17.63 12.38C15.55 12.38 13.88 14.05 13.88 16.13C13.88 18.2 15.55 19.88 17.63 19.88Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.38 19.88C8.45 19.88 10.13 18.2 10.13 16.13C10.13 14.05 8.45 12.38 6.38 12.38C4.3 12.38 2.63 14.05 2.63 16.13C2.63 18.2 4.3 19.88 6.38 19.88Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  clock: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          fill="currentColor"
        />
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M12 6.75V12H17.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'credit-card': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M2.25 9.08H21.75L21.75 18C21.75 18.2 21.67 18.39 21.53 18.53C21.39 18.67 21.2 18.75 21 18.75H3C2.8 18.75 2.61 18.67 2.47 18.53C2.33 18.39 2.25 18.2 2.25 18L2.25 9.08Z"
          fill="currentColor"
        />
        <path
          d="M21 5.25H3C2.59 5.25 2.25 5.59 2.25 6V18C2.25 18.41 2.59 18.75 3 18.75H21C21.41 18.75 21.75 18.41 21.75 18V6C21.75 5.59 21.41 5.25 21 5.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.75 15.75H18.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.25 15.75H12.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2.25 9.08H21.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  download: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M21 12H3C2.59 12 2.25 12.34 2.25 12.75V18.75C2.25 19.16 2.59 19.5 3 19.5H21C21.41 19.5 21.75 19.16 21.75 18.75V12.75C21.75 12.34 21.41 12 21 12Z"
          fill="currentColor"
        />
        <path
          d="M16.5 12H21C21.2 12 21.39 12.08 21.53 12.22C21.67 12.36 21.75 12.55 21.75 12.75V18.75C21.75 18.95 21.67 19.14 21.53 19.28C21.39 19.42 21.2 19.5 21 19.5H3C2.8 19.5 2.61 19.42 2.47 19.28C2.33 19.14 2.25 18.95 2.25 18.75V12.75C2.25 12.55 2.33 12.36 2.47 12.22C2.61 12.08 2.8 12 3 12H7.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 2.25V12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.5 7.5L12 12L16.5 7.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.63 16.88C18.25 16.88 18.75 16.37 18.75 15.75C18.75 15.13 18.25 14.63 17.63 14.63C17 14.63 16.5 15.13 16.5 15.75C16.5 16.37 17 16.88 17.63 16.88Z"
          fill="currentColor"
        />
      </>
    ),
  },
  gift: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M19.5 12V18.75C19.5 18.95 19.42 19.14 19.28 19.28C19.14 19.42 18.95 19.5 18.75 19.5H5.25C5.05 19.5 4.86 19.42 4.72 19.28C4.58 19.14 4.5 18.95 4.5 18.75V12H19.5Z"
          fill="currentColor"
        />
        <path
          d="M20.25 6.75H16.96C17 6.72 17.04 6.69 17.07 6.66C17.36 6.4 17.59 6.09 17.75 5.75C17.9 5.4 17.99 5.03 18 4.65C18.01 4.23 17.94 3.82 17.79 3.43C17.63 3.04 17.4 2.69 17.11 2.39C16.81 2.1 16.46 1.87 16.07 1.71C15.69 1.56 15.27 1.49 14.85 1.5C14.47 1.51 14.1 1.59 13.75 1.75C13.41 1.91 13.1 2.14 12.84 2.43C12.49 2.83 12.21 3.29 12 3.79C11.79 3.29 11.51 2.83 11.16 2.43C10.9 2.14 10.59 1.91 10.25 1.75C9.9 1.59 9.53 1.51 9.15 1.5C8.73 1.49 8.32 1.56 7.93 1.71C7.54 1.87 7.19 2.1 6.89 2.39C6.6 2.69 6.37 3.04 6.21 3.43C6.06 3.82 5.99 4.23 6 4.65C6.01 5.03 6.1 5.4 6.26 5.75C6.41 6.09 6.64 6.4 6.93 6.66C6.96 6.69 7 6.72 7.04 6.75H3.75C3.35 6.75 2.97 6.91 2.69 7.19C2.41 7.47 2.25 7.85 2.25 8.25V11.25C2.25 11.65 2.41 12.03 2.69 12.31C2.97 12.59 3.35 12.75 3.75 12.75V18.75C3.75 19.15 3.91 19.53 4.19 19.81C4.47 20.09 4.85 20.25 5.25 20.25H18.75C19.15 20.25 19.53 20.09 19.81 19.81C20.09 19.53 20.25 19.15 20.25 18.75V12.75C20.65 12.75 21.03 12.59 21.31 12.31C21.59 12.03 21.75 11.65 21.75 11.25V8.25C21.75 7.85 21.59 7.47 21.31 7.19C21.03 6.91 20.65 6.75 20.25 6.75ZM13.97 3.42C14.09 3.29 14.23 3.19 14.39 3.11C14.55 3.04 14.73 3 14.91 3H14.95C15.16 3 15.36 3.04 15.56 3.13C15.75 3.21 15.92 3.33 16.06 3.48C16.21 3.62 16.32 3.8 16.39 3.99C16.47 4.19 16.51 4.39 16.5 4.6C16.5 4.78 16.46 4.95 16.39 5.11C16.31 5.28 16.21 5.42 16.08 5.54C15.19 6.33 13.71 6.6 12.8 6.7C12.91 5.71 13.22 4.27 13.97 3.42ZM7.96 3.46C8.25 3.17 8.64 3 9.06 3H9.1C9.28 3 9.45 3.04 9.61 3.11C9.78 3.19 9.92 3.29 10.04 3.42C10.83 4.31 11.1 5.78 11.2 6.7C10.29 6.6 8.82 6.32 7.93 5.53C7.8 5.42 7.69 5.27 7.62 5.11C7.55 4.95 7.51 4.77 7.5 4.6C7.5 4.39 7.54 4.18 7.61 3.98C7.69 3.78 7.81 3.61 7.96 3.46H7.96ZM3.75 8.25H11.25V11.25H3.75V8.25ZM5.25 12.75H11.25V18.75H5.25V12.75ZM18.75 18.75H12.75V12.75H18.75V18.75ZM20.25 11.25H12.75V8.25H20.25V11.25Z"
          fill="currentColor"
        />
      </>
    ),
  },
  heart: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 20.25C12 20.25 2.63 15 2.63 8.63C2.63 7.5 3.02 6.41 3.73 5.53C4.44 4.66 5.44 4.07 6.54 3.85C7.65 3.62 8.8 3.79 9.79 4.32C10.78 4.85 11.57 5.71 12 6.75L12 6.75C12.43 5.71 13.22 4.85 14.21 4.32C15.2 3.79 16.35 3.62 17.46 3.85C18.56 4.07 19.56 4.66 20.27 5.53C20.98 6.41 21.37 7.5 21.38 8.63C21.38 15 12 20.25 12 20.25Z"
          fill="currentColor"
        />
        <path
          d="M12 20.25C12 20.25 2.63 15 2.63 8.63C2.63 7.5 3.02 6.41 3.73 5.53C4.44 4.66 5.44 4.07 6.54 3.85C7.65 3.62 8.8 3.79 9.79 4.32C10.78 4.85 11.57 5.71 12 6.75L12 6.75C12.43 5.71 13.22 4.85 14.21 4.32C15.2 3.79 16.35 3.62 17.46 3.85C18.56 4.07 19.56 4.66 20.27 5.53C20.98 6.41 21.37 7.5 21.38 8.63C21.38 15 12 20.25 12 20.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  house: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M14.25 20.25V14.25H9.75V20.25L3.75 20.25V10.83C3.75 10.73 3.77 10.62 3.81 10.53C3.85 10.43 3.92 10.35 3.99 10.28L11.49 3.46C11.63 3.33 11.81 3.26 12 3.26C12.18 3.26 12.36 3.33 12.5 3.46L20 10.28C20.08 10.35 20.14 10.43 20.18 10.53C20.23 10.62 20.25 10.73 20.25 10.83V20.25L14.25 20.25Z"
          fill="currentColor"
        />
        <path
          d="M20.25 20.25V10.83C20.25 10.73 20.23 10.62 20.18 10.53C20.14 10.43 20.08 10.35 20 10.28L12.5 3.46C12.36 3.33 12.18 3.26 12 3.26C11.81 3.26 11.63 3.33 11.49 3.46L3.99 10.28C3.92 10.35 3.85 10.43 3.81 10.53C3.77 10.62 3.75 10.73 3.75 10.83V20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.5 20.25H22.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.25 20.25V14.25H9.75V20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  key: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M8.73 11.52C8.16 10.07 8.09 8.48 8.56 6.99C9.02 5.51 9.98 4.23 11.28 3.38C12.58 2.52 14.13 2.14 15.67 2.3C17.22 2.45 18.66 3.14 19.76 4.24C20.86 5.34 21.55 6.78 21.7 8.33C21.86 9.87 21.48 11.42 20.62 12.72C19.77 14.02 18.49 14.98 17.01 15.44C15.52 15.91 13.93 15.84 12.48 15.27L12.49 15.27L11.25 16.5H9V18.75H6.75V21H3V17.25L8.73 11.52L8.73 11.52Z"
          fill="currentColor"
        />
        <path
          d="M8.73 11.52C8.16 10.07 8.09 8.48 8.56 6.99C9.02 5.51 9.98 4.23 11.28 3.38C12.58 2.52 14.13 2.14 15.67 2.3C17.22 2.45 18.66 3.14 19.76 4.24C20.86 5.34 21.55 6.78 21.7 8.33C21.86 9.87 21.48 11.42 20.62 12.72C19.77 14.02 18.49 14.98 17.01 15.44C15.52 15.91 13.93 15.84 12.48 15.27L12.49 15.27L11.25 16.5H9V18.75H6.75V21H3V17.25L8.73 11.52L8.73 11.52Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.88 8.25C17.5 8.25 18 7.75 18 7.13C18 6.5 17.5 6 16.88 6C16.25 6 15.75 6.5 15.75 7.13C15.75 7.75 16.25 8.25 16.88 8.25Z"
          fill="currentColor"
        />
      </>
    ),
  },
  lightbulb: {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M13.75 18.13C13.75 18.29 13.68 18.45 13.57 18.57C13.45 18.68 13.29 18.75 13.13 18.75H6.88C6.71 18.75 6.55 18.68 6.43 18.57C6.32 18.45 6.25 18.29 6.25 18.13C6.25 17.96 6.32 17.8 6.43 17.68C6.55 17.57 6.71 17.5 6.88 17.5H13.13C13.29 17.5 13.45 17.57 13.57 17.68C13.68 17.8 13.75 17.96 13.75 18.13ZM16.88 8.13C16.88 9.17 16.64 10.2 16.19 11.13C15.73 12.07 15.07 12.89 14.25 13.53C14.09 13.65 13.97 13.8 13.88 13.97C13.8 14.15 13.75 14.34 13.75 14.53V15C13.75 15.33 13.62 15.65 13.38 15.88C13.15 16.12 12.83 16.25 12.5 16.25H7.5C7.17 16.25 6.85 16.12 6.62 15.88C6.38 15.65 6.25 15.33 6.25 15V14.53C6.25 14.34 6.21 14.15 6.12 13.98C6.04 13.81 5.91 13.66 5.76 13.54C4.94 12.9 4.28 12.09 3.82 11.16C3.37 10.22 3.13 9.2 3.13 8.16C3.1 4.44 6.11 1.34 9.83 1.25C10.75 1.23 11.66 1.39 12.52 1.73C13.37 2.06 14.15 2.56 14.8 3.2C15.46 3.84 15.98 4.61 16.34 5.46C16.69 6.3 16.88 7.21 16.88 8.13ZM15.63 8.13C15.63 7.37 15.48 6.63 15.18 5.94C14.89 5.25 14.47 4.62 13.93 4.1C13.39 3.57 12.76 3.16 12.06 2.89C11.36 2.61 10.61 2.48 9.86 2.5C6.82 2.57 4.36 5.11 4.38 8.16C4.38 9 4.57 9.84 4.95 10.6C5.32 11.37 5.86 12.03 6.53 12.55C6.84 12.79 7.08 13.09 7.25 13.43C7.41 13.77 7.5 14.15 7.5 14.53V15H12.5V14.53C12.5 14.15 12.59 13.77 12.76 13.43C12.93 13.09 13.17 12.79 13.47 12.55C14.15 12.03 14.69 11.35 15.06 10.59C15.43 9.82 15.63 8.98 15.63 8.13ZM14.37 7.4C14.2 6.49 13.77 5.66 13.12 5.01C12.47 4.36 11.63 3.92 10.73 3.76C10.65 3.74 10.57 3.75 10.49 3.77C10.41 3.78 10.33 3.82 10.26 3.87C10.2 3.91 10.14 3.97 10.1 4.04C10.05 4.11 10.02 4.19 10.01 4.27C10 4.35 10 4.43 10.02 4.51C10.03 4.59 10.07 4.67 10.12 4.74C10.16 4.8 10.22 4.86 10.29 4.9C10.36 4.95 10.44 4.98 10.52 4.99C11.82 5.21 12.91 6.31 13.13 7.6C13.16 7.75 13.23 7.88 13.35 7.98C13.46 8.07 13.6 8.13 13.75 8.13C13.79 8.12 13.82 8.12 13.86 8.12C14.02 8.09 14.16 8 14.26 7.86C14.36 7.73 14.39 7.56 14.37 7.4Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'lock-key': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M19.5 8.25H4.5C4.3 8.25 4.11 8.33 3.97 8.47C3.83 8.61 3.75 8.8 3.75 9V19.5C3.75 19.7 3.83 19.89 3.97 20.03C4.11 20.17 4.3 20.25 4.5 20.25H19.5C19.7 20.25 19.89 20.17 20.03 20.03C20.17 19.89 20.25 19.7 20.25 19.5V9C20.25 8.8 20.17 8.61 20.03 8.47C19.89 8.33 19.7 8.25 19.5 8.25ZM12 15C11.63 15 11.27 14.89 10.96 14.68C10.65 14.48 10.41 14.19 10.27 13.84C10.12 13.5 10.09 13.12 10.16 12.76C10.23 12.4 10.41 12.06 10.67 11.8C10.94 11.54 11.27 11.36 11.63 11.29C12 11.21 12.37 11.25 12.72 11.39C13.06 11.53 13.35 11.78 13.56 12.08C13.76 12.39 13.87 12.75 13.87 13.13C13.87 13.62 13.68 14.1 13.32 14.45C12.97 14.8 12.5 15 12 15Z"
          fill="currentColor"
        />
        <path
          d="M12 15C13.03 15 13.87 14.16 13.87 13.13C13.87 12.09 13.03 11.25 12 11.25C10.96 11.25 10.12 12.09 10.12 13.13C10.12 14.16 10.96 15 12 15Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 15V17.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19.5 8.25H4.5C4.08 8.25 3.75 8.59 3.75 9V19.5C3.75 19.91 4.08 20.25 4.5 20.25H19.5C19.91 20.25 20.25 19.91 20.25 19.5V9C20.25 8.59 19.91 8.25 19.5 8.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8.62 8.25V4.88C8.62 3.98 8.98 3.12 9.61 2.49C10.25 1.86 11.1 1.5 12 1.5C12.89 1.5 13.75 1.86 14.39 2.49C15.02 3.12 15.37 3.98 15.37 4.88V8.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'map-pin': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M12 2.25C10.01 2.25 8.1 3.04 6.7 4.45C5.29 5.85 4.5 7.76 4.5 9.75C4.5 16.5 12 21.75 12 21.75C12 21.75 19.5 16.5 19.5 9.75C19.5 7.76 18.71 5.85 17.3 4.45C15.9 3.04 13.99 2.25 12 2.25ZM12 12.75C11.41 12.75 10.83 12.57 10.33 12.24C9.84 11.91 9.46 11.45 9.23 10.9C9 10.35 8.94 9.75 9.06 9.16C9.17 8.58 9.46 8.05 9.88 7.63C10.3 7.21 10.83 6.92 11.41 6.81C12 6.69 12.6 6.75 13.15 6.98C13.7 7.21 14.16 7.59 14.49 8.08C14.82 8.58 15 9.16 15 9.75C15 10.55 14.68 11.31 14.12 11.87C13.56 12.43 12.8 12.75 12 12.75Z"
          fill="currentColor"
        />
        <path
          d="M18.75 21H14.11C14.89 20.3 15.63 19.56 16.31 18.77C18.89 15.81 20.25 12.69 20.25 9.75C20.25 7.56 19.38 5.46 17.83 3.92C16.29 2.37 14.19 1.5 12 1.5C9.81 1.5 7.71 2.37 6.17 3.92C4.62 5.46 3.75 7.56 3.75 9.75C3.75 12.69 5.11 15.81 7.69 18.77C8.37 19.56 9.11 20.3 9.89 21H5.25C5.05 21 4.86 21.08 4.72 21.22C4.58 21.36 4.5 21.55 4.5 21.75C4.5 21.95 4.58 22.14 4.72 22.28C4.86 22.42 5.05 22.5 5.25 22.5H18.75C18.95 22.5 19.14 22.42 19.28 22.28C19.42 22.14 19.5 21.95 19.5 21.75C19.5 21.55 19.42 21.36 19.28 21.22C19.14 21.08 18.95 21 18.75 21ZM5.25 9.75C5.25 7.96 5.96 6.24 7.23 4.98C8.49 3.71 10.21 3 12 3C13.79 3 15.51 3.71 16.77 4.98C18.04 6.24 18.75 7.96 18.75 9.75C18.75 15.12 13.55 19.59 12 20.81C10.45 19.59 5.25 15.12 5.25 9.75ZM15.75 9.75C15.75 9.01 15.53 8.28 15.12 7.67C14.71 7.05 14.12 6.57 13.44 6.29C12.75 6 12 5.93 11.27 6.07C10.54 6.22 9.87 6.57 9.35 7.1C8.82 7.62 8.47 8.29 8.32 9.02C8.18 9.75 8.25 10.5 8.54 11.19C8.82 11.87 9.3 12.46 9.92 12.87C10.53 13.28 11.26 13.5 12 13.5C12.99 13.5 13.95 13.1 14.65 12.4C15.35 11.7 15.75 10.74 15.75 9.75ZM9.75 9.75C9.75 9.3 9.88 8.87 10.13 8.5C10.38 8.13 10.73 7.84 11.14 7.67C11.55 7.5 12 7.46 12.44 7.54C12.88 7.63 13.28 7.84 13.59 8.16C13.91 8.47 14.12 8.87 14.21 9.31C14.29 9.75 14.25 10.2 14.08 10.61C13.91 11.02 13.62 11.37 13.25 11.62C12.88 11.87 12.45 12 12 12C11.4 12 10.83 11.76 10.41 11.34C9.99 10.92 9.75 10.35 9.75 9.75Z"
          fill="currentColor"
        />
      </>
    ),
  },
  menu: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M4 15H14"
          stroke="currentColor"
          strokeWidth="1.49"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4 9H20.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  minus: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M3.75 12H20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'minus-20': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M17.5 10C17.5 10.17 17.43 10.32 17.32 10.44C17.2 10.56 17.04 10.63 16.88 10.63H3.13C2.96 10.63 2.8 10.56 2.68 10.44C2.57 10.32 2.5 10.17 2.5 10C2.5 9.83 2.57 9.68 2.68 9.56C2.8 9.44 2.96 9.38 3.13 9.38H16.88C17.04 9.38 17.2 9.44 17.32 9.56C17.43 9.68 17.5 9.83 17.5 10Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'minus-circle': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          fill="currentColor"
        />
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M8.25 12H15.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  notebook: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M7.5 20.25H4.5C4.3 20.25 4.11 20.17 3.97 20.03C3.83 19.89 3.75 19.7 3.75 19.5V4.5C3.75 4.3 3.83 4.11 3.97 3.97C4.11 3.83 4.3 3.75 4.5 3.75H7.5V20.25Z"
          fill="currentColor"
        />
        <path
          d="M10.5 10.5H16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.5 13.5H16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19.5 3.75H4.5C4.09 3.75 3.75 4.09 3.75 4.5V19.5C3.75 19.91 4.09 20.25 4.5 20.25H19.5C19.91 20.25 20.25 19.91 20.25 19.5V4.5C20.25 4.09 19.91 3.75 19.5 3.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.5 3.75V20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  nut: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M20.61 6.86L12.36 2.34C12.25 2.28 12.13 2.25 12 2.25C11.87 2.25 11.75 2.28 11.64 2.34L3.39 6.86C3.27 6.93 3.17 7.02 3.11 7.14C3.04 7.25 3 7.38 3 7.52V16.48C3 16.62 3.04 16.75 3.11 16.86C3.17 16.98 3.27 17.07 3.39 17.14L11.64 21.66C11.75 21.72 11.87 21.75 12 21.75C12.13 21.75 12.25 21.72 12.36 21.66L20.61 17.14C20.73 17.07 20.83 16.98 20.89 16.86C20.96 16.75 21 16.62 21 16.48V7.52C21 7.38 20.96 7.25 20.9 7.13C20.83 7.02 20.73 6.92 20.61 6.86ZM12 15.75C11.26 15.75 10.53 15.53 9.92 15.12C9.3 14.71 8.82 14.12 8.54 13.44C8.25 12.75 8.18 12 8.32 11.27C8.47 10.54 8.82 9.87 9.35 9.35C9.87 8.82 10.54 8.47 11.27 8.32C12 8.18 12.75 8.25 13.44 8.54C14.12 8.82 14.71 9.3 15.12 9.92C15.53 10.53 15.75 11.26 15.75 12C15.75 12.99 15.35 13.95 14.65 14.65C13.95 15.35 12.99 15.75 12 15.75Z"
          fill="currentColor"
        />
        <path
          d="M12 7.5C11.11 7.5 10.24 7.76 9.5 8.26C8.76 8.75 8.18 9.46 7.84 10.28C7.5 11.1 7.41 12.01 7.59 12.88C7.76 13.75 8.19 14.55 8.82 15.18C9.45 15.81 10.25 16.24 11.12 16.41C11.99 16.59 12.9 16.5 13.72 16.16C14.54 15.82 15.25 15.24 15.74 14.5C16.24 13.76 16.5 12.89 16.5 12C16.5 10.81 16.02 9.66 15.18 8.82C14.34 7.98 13.19 7.5 12 7.5ZM12 15C11.41 15 10.83 14.82 10.33 14.49C9.84 14.16 9.46 13.7 9.23 13.15C9 12.6 8.94 12 9.06 11.41C9.17 10.83 9.46 10.3 9.88 9.88C10.3 9.46 10.83 9.17 11.41 9.06C12 8.94 12.6 9 13.15 9.23C13.7 9.46 14.16 9.84 14.49 10.33C14.82 10.83 15 11.41 15 12C15 12.8 14.68 13.56 14.12 14.12C13.56 14.68 12.8 15 12 15ZM20.97 6.2L12.72 1.69C12.5 1.57 12.25 1.5 12 1.5C11.75 1.5 11.5 1.57 11.28 1.69L3.03 6.2C2.79 6.33 2.6 6.52 2.46 6.75C2.32 6.98 2.25 7.25 2.25 7.52V16.48C2.25 16.75 2.32 17.01 2.46 17.25C2.6 17.48 2.79 17.67 3.03 17.79L11.28 22.31C11.5 22.43 11.75 22.5 12 22.5C12.25 22.5 12.5 22.43 12.72 22.31L20.97 17.79C21.21 17.67 21.4 17.48 21.54 17.25C21.68 17.01 21.75 16.75 21.75 16.48V7.52C21.75 7.25 21.68 6.98 21.54 6.75C21.4 6.52 21.21 6.33 20.97 6.2ZM12 21L3.75 16.48V7.52L12 3L20.25 7.52V16.48L12 21Z"
          fill="currentColor"
        />
      </>
    ),
  },
  outward: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M17.48 15.1C17.35 14.97 17.26 14.79 17.26 14.57L17.26 6.74L9.43 6.74C9.02 6.74 8.68 6.4 8.68 5.99C8.68 5.58 9.02 5.24 9.43 5.24L18.01 5.24C18.42 5.24 18.76 5.58 18.76 5.99L18.76 14.57C18.76 14.98 18.42 15.32 18.01 15.32C17.81 15.33 17.61 15.24 17.48 15.1Z"
          fill="currentColor"
        />
        <path
          d="M5.46 18.54C5.17 18.25 5.17 17.77 5.46 17.48L17.36 5.58C17.65 5.29 18.13 5.29 18.42 5.58C18.71 5.87 18.71 6.35 18.42 6.64L6.52 18.54C6.23 18.83 5.75 18.83 5.46 18.54Z"
          fill="currentColor"
        />
      </>
    ),
  },
  package: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M11.99 21.64C11.86 21.64 11.73 21.61 11.62 21.54L3.75 17.09C3.63 17.02 3.53 16.93 3.47 16.82C3.4 16.7 3.36 16.57 3.36 16.44V7.56C3.36 7.43 3.4 7.3 3.47 7.18L3.47 7.18L12.09 12L11.99 21.64L11.99 21.64Z"
          fill="currentColor"
        />
        <path
          d="M12.09 12L11.99 21.64"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.61 16.44V7.56C20.61 7.43 20.58 7.3 20.51 7.18C20.45 7.07 20.35 6.98 20.23 6.91L12.36 2.46C12.25 2.39 12.12 2.36 11.99 2.36C11.86 2.36 11.73 2.39 11.62 2.46L3.75 6.91C3.63 6.98 3.53 7.07 3.47 7.18C3.4 7.3 3.36 7.43 3.36 7.56V16.44C3.36 16.57 3.4 16.7 3.47 16.82C3.53 16.93 3.63 17.02 3.75 17.09L11.62 21.54C11.73 21.61 11.86 21.64 11.99 21.64C12.12 21.64 12.25 21.61 12.36 21.54L20.23 17.09C20.35 17.02 20.45 16.93 20.51 16.82C20.58 16.7 20.61 16.57 20.61 16.44V16.44Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.51 7.18L12.09 12L3.47 7.18"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.6 13.92V9.42L7.9 4.56"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'paint-bucket': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M23.25 19.5C23.25 19.9 23.09 20.28 22.81 20.56C22.53 20.84 22.15 21 21.75 21C21.35 21 20.97 20.84 20.69 20.56C20.41 20.28 20.25 19.9 20.25 19.5C20.25 18 21.75 15.75 21.75 15.75C21.75 15.75 23.25 18 23.25 19.5ZM21.75 12.33L19.5 13.08L12.02 20.56C11.88 20.7 11.71 20.81 11.53 20.89C11.35 20.96 11.15 21 10.95 21C10.76 21 10.56 20.96 10.38 20.89C10.2 20.81 10.03 20.7 9.89 20.56L1.94 12.61C1.8 12.47 1.69 12.3 1.61 12.12C1.54 11.94 1.5 11.74 1.5 11.54C1.5 11.35 1.54 11.15 1.61 10.97C1.69 10.79 1.8 10.62 1.94 10.48L10.92 1.5L21.75 12.33ZM12.95 9.55C12.69 9.29 12.35 9.11 11.99 9.04C11.63 8.96 11.25 9 10.91 9.14C10.56 9.28 10.27 9.52 10.07 9.83C9.86 10.14 9.75 10.5 9.75 10.88C9.75 11.25 9.86 11.61 10.07 11.92C10.27 12.23 10.56 12.47 10.91 12.61C11.25 12.75 11.63 12.79 11.99 12.71C12.35 12.64 12.69 12.46 12.95 12.2C13.3 11.85 13.5 11.37 13.5 10.88C13.5 10.38 13.3 9.9 12.95 9.55Z"
          fill="currentColor"
        />
        <path
          d="M22.37 15.33C22.31 15.23 22.21 15.15 22.1 15.09C22 15.03 21.87 15 21.75 15C21.63 15 21.5 15.03 21.4 15.09C21.29 15.15 21.19 15.23 21.13 15.33C20.96 15.58 19.5 17.82 19.5 19.5C19.5 20.1 19.74 20.67 20.16 21.09C20.58 21.51 21.15 21.75 21.75 21.75C22.35 21.75 22.92 21.51 23.34 21.09C23.76 20.67 24 20.1 24 19.5C24 17.82 22.54 15.58 22.37 15.33ZM21.75 20.25C21.55 20.25 21.36 20.17 21.22 20.03C21.08 19.89 21 19.7 21 19.5C21 18.86 21.37 17.97 21.75 17.24C22.12 17.97 22.5 18.87 22.5 19.5C22.5 19.7 22.42 19.89 22.28 20.03C22.14 20.17 21.95 20.25 21.75 20.25ZM21.99 13.04C22.11 13 22.22 12.93 22.3 12.83C22.39 12.74 22.45 12.62 22.48 12.5C22.51 12.37 22.51 12.24 22.47 12.12C22.44 12 22.37 11.89 22.28 11.8L11.45 0.97C11.31 0.83 11.12 0.75 10.92 0.75C10.72 0.75 10.53 0.83 10.39 0.97L6.59 4.78L4.28 2.47C4.14 2.33 3.95 2.25 3.75 2.25C3.55 2.25 3.36 2.33 3.22 2.47C3.08 2.61 3 2.8 3 3C3 3.2 3.08 3.39 3.22 3.53L5.53 5.84L1.41 9.95C0.98 10.38 0.75 10.95 0.75 11.54C0.75 12.14 0.98 12.71 1.41 13.14L9.36 21.09C9.79 21.52 10.36 21.75 10.96 21.75C11.55 21.75 12.12 21.52 12.55 21.09L19.9 13.74L21.99 13.04ZM18.97 12.55L11.49 20.03C11.34 20.17 11.15 20.25 10.95 20.25C10.76 20.25 10.57 20.17 10.42 20.03L2.47 12.07C2.33 11.93 2.25 11.74 2.25 11.54C2.25 11.35 2.33 11.16 2.47 11.01L6.59 6.9L9.32 9.63C9.02 10.18 8.93 10.83 9.06 11.44C9.2 12.06 9.55 12.6 10.06 12.98C10.56 13.36 11.19 13.54 11.82 13.49C12.45 13.45 13.04 13.18 13.48 12.73C13.93 12.28 14.2 11.69 14.24 11.06C14.29 10.43 14.11 9.81 13.73 9.3C13.35 8.8 12.81 8.45 12.19 8.31C11.57 8.18 10.93 8.27 10.38 8.57L7.65 5.84L10.93 2.56L20.36 12L19.26 12.37C19.15 12.4 19.05 12.46 18.97 12.55ZM10.83 10.08C11.04 9.83 11.34 9.67 11.67 9.64C12 9.61 12.33 9.71 12.58 9.92C12.83 10.14 12.99 10.44 13.02 10.77C13.05 11.1 12.95 11.42 12.74 11.67C12.53 11.93 12.22 12.09 11.89 12.12C11.57 12.15 11.24 12.05 10.99 11.83C10.73 11.62 10.57 11.32 10.54 10.99C10.51 10.67 10.61 10.34 10.82 10.09C10.82 10.09 10.83 10.08 10.83 10.08Z"
          fill="currentColor"
        />
      </>
    ),
  },
  percent: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M19.28 5.78L5.78 19.28C5.64 19.42 5.45 19.5 5.25 19.5C5.05 19.5 4.86 19.42 4.72 19.28C4.58 19.14 4.5 18.95 4.5 18.75C4.5 18.55 4.58 18.36 4.72 18.22L18.22 4.72C18.36 4.58 18.55 4.5 18.75 4.5C18.95 4.5 19.14 4.58 19.28 4.72C19.42 4.86 19.5 5.05 19.5 5.25C19.5 5.45 19.42 5.64 19.28 5.78V5.78ZM4.74 9.51C4.11 8.88 3.75 8.02 3.75 7.12C3.75 6.23 4.11 5.37 4.74 4.74C5.37 4.1 6.23 3.75 7.13 3.75C8.02 3.75 8.88 4.1 9.51 4.74C10.15 5.37 10.5 6.23 10.5 7.12C10.5 8.02 10.14 8.88 9.51 9.51C8.88 10.14 8.02 10.5 7.12 10.5C6.23 10.5 5.37 10.14 4.74 9.51ZM5.25 7.13C5.25 7.43 5.33 7.74 5.47 8.01C5.62 8.28 5.83 8.51 6.08 8.68C6.34 8.85 6.64 8.96 6.94 8.99C7.25 9.02 7.56 8.97 7.84 8.86C8.13 8.74 8.38 8.55 8.58 8.31C8.77 8.07 8.9 7.79 8.96 7.49C9.02 7.19 9.01 6.87 8.92 6.58C8.83 6.28 8.67 6.02 8.45 5.8C8.19 5.54 7.85 5.36 7.49 5.28C7.13 5.21 6.75 5.25 6.41 5.39C6.06 5.53 5.77 5.77 5.57 6.08C5.36 6.39 5.25 6.75 5.25 7.13ZM20.25 16.88C20.25 17.66 19.98 18.41 19.48 19.02C18.99 19.62 18.3 20.03 17.53 20.18C16.77 20.34 15.97 20.22 15.28 19.85C14.6 19.48 14.06 18.89 13.76 18.17C13.46 17.44 13.42 16.64 13.65 15.89C13.87 15.15 14.35 14.5 15 14.07C15.65 13.63 16.43 13.44 17.21 13.52C17.98 13.59 18.71 13.94 19.26 14.49C19.58 14.8 19.83 15.17 20 15.58C20.16 15.99 20.25 16.43 20.25 16.88ZM18.75 16.88C18.75 16.44 18.6 16.02 18.32 15.69C18.05 15.35 17.67 15.12 17.24 15.04C16.82 14.95 16.37 15.02 15.99 15.22C15.61 15.43 15.31 15.76 15.14 16.16C14.98 16.56 14.96 17 15.08 17.42C15.21 17.83 15.47 18.19 15.83 18.43C16.19 18.67 16.63 18.78 17.06 18.74C17.49 18.7 17.89 18.51 18.2 18.2C18.38 18.03 18.51 17.82 18.61 17.59C18.7 17.37 18.75 17.12 18.75 16.88Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'percent-duotone': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M16.88 19.5C18.32 19.5 19.5 18.32 19.5 16.88C19.5 15.43 18.32 14.25 16.88 14.25C15.43 14.25 14.25 15.43 14.25 16.88C14.25 18.32 15.43 19.5 16.88 19.5Z"
          fill="currentColor"
        />
        <path
          opacity="0.2"
          d="M7.13 9.75C8.57 9.75 9.75 8.57 9.75 7.13C9.75 5.68 8.57 4.5 7.13 4.5C5.68 4.5 4.5 5.68 4.5 7.13C4.5 8.57 5.68 9.75 7.13 9.75Z"
          fill="currentColor"
        />
        <path
          d="M18.75 5.25L5.25 18.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.13 9.75C8.57 9.75 9.75 8.57 9.75 7.13C9.75 5.68 8.57 4.5 7.13 4.5C5.68 4.5 4.5 5.68 4.5 7.13C4.5 8.57 5.68 9.75 7.13 9.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M16.88 19.5C18.32 19.5 19.5 18.32 19.5 16.88C19.5 15.43 18.32 14.25 16.88 14.25C15.43 14.25 14.25 15.43 14.25 16.88C14.25 18.32 15.43 19.5 16.88 19.5Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
      </>
    ),
  },
  phone: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M8.67 11.7C9.45 13.29 10.74 14.58 12.33 15.35C12.45 15.4 12.58 15.43 12.71 15.42C12.83 15.41 12.96 15.36 13.06 15.29L15.41 13.73C15.52 13.66 15.64 13.61 15.76 13.6C15.88 13.59 16.01 13.61 16.12 13.66L20.51 15.54C20.66 15.61 20.79 15.72 20.87 15.86C20.95 16 20.98 16.16 20.96 16.32C20.83 17.41 20.3 18.4 19.47 19.13C18.65 19.85 17.59 20.25 16.5 20.25C13.12 20.25 9.88 18.91 7.48 16.52C5.09 14.12 3.75 10.88 3.75 7.5C3.75 6.41 4.15 5.35 4.87 4.53C5.6 3.7 6.59 3.17 7.68 3.04C7.84 3.02 8 3.05 8.14 3.13C8.28 3.21 8.39 3.34 8.46 3.49L10.34 7.88C10.39 7.99 10.41 8.12 10.4 8.24C10.39 8.36 10.35 8.48 10.28 8.59L8.72 10.97C8.65 11.08 8.61 11.2 8.6 11.33C8.59 11.46 8.61 11.59 8.67 11.7Z"
          fill="currentColor"
        />
        <path
          d="M8.67 11.7C9.45 13.29 10.74 14.58 12.33 15.35C12.45 15.4 12.58 15.43 12.71 15.42C12.83 15.41 12.96 15.36 13.06 15.29L15.41 13.73C15.52 13.66 15.64 13.61 15.76 13.6C15.88 13.59 16.01 13.61 16.12 13.66L20.51 15.54C20.66 15.61 20.79 15.72 20.87 15.86C20.95 16 20.98 16.16 20.96 16.32C20.83 17.41 20.3 18.4 19.47 19.13C18.65 19.85 17.59 20.25 16.5 20.25C13.12 20.25 9.88 18.91 7.48 16.52C5.09 14.12 3.75 10.88 3.75 7.5C3.75 6.41 4.15 5.35 4.87 4.53C5.6 3.7 6.59 3.17 7.68 3.04C7.84 3.02 8 3.05 8.14 3.13C8.28 3.21 8.39 3.34 8.46 3.49L10.34 7.88C10.39 7.99 10.41 8.12 10.4 8.24C10.39 8.36 10.35 8.48 10.28 8.59L8.72 10.97C8.65 11.08 8.61 11.2 8.6 11.33C8.59 11.46 8.61 11.59 8.67 11.7V11.7Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.95 3.75C16.22 4.09 17.38 4.76 18.31 5.69C19.24 6.62 19.91 7.78 20.25 9.05"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.17 6.65C14.93 6.85 15.63 7.26 16.19 7.81C16.74 8.37 17.15 9.07 17.35 9.83"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  /** Figma „PhoneCall“ (top bar): brand-red handset fill with an outline in the text colour. */
  'phone-call': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M7.22 9.75C7.87 11.08 8.95 12.15 10.28 12.79C10.37 12.83 10.48 12.85 10.59 12.85C10.69 12.84 10.8 12.8 10.89 12.74L12.84 11.44C12.93 11.38 13.03 11.35 13.13 11.34C13.24 11.33 13.34 11.34 13.44 11.38L17.1 12.95C17.22 13 17.32 13.1 17.39 13.21C17.46 13.33 17.49 13.47 17.47 13.6C17.35 14.5 16.91 15.34 16.23 15.94C15.54 16.54 14.66 16.88 13.75 16.88C10.93 16.88 8.23 15.76 6.24 13.76C4.24 11.77 3.12 9.07 3.12 6.25C3.13 5.34 3.46 4.46 4.06 3.77C4.66 3.09 5.5 2.65 6.4 2.53C6.53 2.51 6.67 2.54 6.79 2.61C6.9 2.68 7 2.78 7.05 2.9L8.62 6.57C8.66 6.66 8.68 6.77 8.67 6.87C8.66 6.97 8.62 7.07 8.57 7.16L7.27 9.14C7.21 9.23 7.17 9.33 7.17 9.44C7.16 9.55 7.18 9.65 7.22 9.75Z"
          fill="#D7191F"
        />
        <path
          d="M7.22 9.75C7.87 11.08 8.95 12.15 10.28 12.79C10.37 12.83 10.48 12.85 10.59 12.85C10.69 12.84 10.8 12.8 10.89 12.74L12.84 11.44C12.93 11.38 13.03 11.35 13.13 11.34C13.24 11.33 13.34 11.34 13.44 11.38L17.1 12.95C17.22 13 17.32 13.1 17.39 13.21C17.46 13.33 17.49 13.47 17.47 13.6C17.35 14.5 16.91 15.34 16.23 15.94C15.54 16.54 14.66 16.88 13.75 16.88C10.93 16.88 8.23 15.76 6.24 13.76C4.24 11.77 3.12 9.07 3.12 6.25C3.13 5.34 3.46 4.46 4.06 3.77C4.66 3.09 5.5 2.65 6.4 2.53C6.53 2.51 6.67 2.54 6.79 2.61C6.9 2.68 7 2.78 7.05 2.9L8.62 6.57C8.66 6.66 8.68 6.77 8.67 6.87C8.66 6.97 8.62 7.07 8.57 7.16L7.27 9.14C7.21 9.23 7.17 9.33 7.17 9.44C7.16 9.55 7.18 9.65 7.22 9.75Z"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.45 3.12C13.51 3.41 14.48 3.97 15.26 4.74C16.03 5.52 16.59 6.49 16.88 7.55"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.81 5.54C12.44 5.71 13.02 6.05 13.49 6.51C13.95 6.98 14.29 7.56 14.46 8.19"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'play-circle': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 3C10.22 3 8.48 3.53 7 4.52C5.52 5.51 4.37 6.91 3.69 8.56C3 10.2 2.83 12.01 3.17 13.76C3.52 15.5 4.38 17.11 5.64 18.36C6.89 19.62 8.5 20.48 10.24 20.83C11.99 21.17 13.8 21 15.44 20.31C17.09 19.63 18.49 18.48 19.48 17C20.47 15.52 21 13.78 21 12C21 10.82 20.77 9.65 20.31 8.56C19.86 7.46 19.2 6.47 18.36 5.64C17.53 4.8 16.54 4.14 15.44 3.69C14.35 3.23 13.18 3 12 3ZM10.5 15V9L15 12L10.5 15Z"
          fill="currentColor"
        />
        <path
          d="M12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M15 12L10.5 9V15L15 12Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  plus: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M3.75 12H20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 3.75V20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'plus-20': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M17.5 10C17.5 10.17 17.43 10.32 17.32 10.44C17.2 10.56 17.04 10.63 16.88 10.63H10.63V16.88C10.63 17.04 10.56 17.2 10.44 17.32C10.32 17.43 10.17 17.5 10 17.5C9.83 17.5 9.68 17.43 9.56 17.32C9.44 17.2 9.38 17.04 9.38 16.88V10.63H3.13C2.96 10.63 2.8 10.56 2.68 10.44C2.57 10.32 2.5 10.17 2.5 10C2.5 9.83 2.57 9.68 2.68 9.56C2.8 9.44 2.96 9.38 3.13 9.38H9.38V3.13C9.38 2.96 9.44 2.8 9.56 2.68C9.68 2.57 9.83 2.5 10 2.5C10.17 2.5 10.32 2.57 10.44 2.68C10.56 2.8 10.63 2.96 10.63 3.13V9.38H16.88C17.04 9.38 17.2 9.44 17.32 9.56C17.43 9.68 17.5 9.83 17.5 10Z"
          fill="currentColor"
        />
      </>
    ),
  },
  search: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M21.53 20.47L16.84 15.78C18.2 14.14 18.88 12.05 18.73 9.93C18.59 7.81 17.63 5.82 16.06 4.39C14.49 2.96 12.43 2.18 10.3 2.23C8.18 2.28 6.15 3.15 4.65 4.65C3.15 6.15 2.28 8.18 2.23 10.3C2.18 12.43 2.96 14.49 4.39 16.06C5.82 17.63 7.81 18.59 9.93 18.73C12.05 18.88 14.14 18.2 15.78 16.84L20.47 21.53C20.54 21.6 20.62 21.66 20.71 21.69C20.8 21.73 20.9 21.75 21 21.75C21.1 21.75 21.2 21.73 21.29 21.69C21.38 21.66 21.46 21.6 21.53 21.53C21.6 21.46 21.66 21.38 21.69 21.29C21.73 21.2 21.75 21.1 21.75 21C21.75 20.9 21.73 20.8 21.69 20.71C21.66 20.62 21.6 20.54 21.53 20.47ZM3.75 10.5C3.75 9.16 4.15 7.86 4.89 6.75C5.63 5.64 6.68 4.77 7.92 4.26C9.15 3.75 10.51 3.62 11.82 3.88C13.13 4.14 14.33 4.78 15.27 5.73C16.22 6.67 16.86 7.87 17.12 9.18C17.38 10.49 17.25 11.85 16.74 13.08C16.23 14.32 15.36 15.37 14.25 16.11C13.14 16.85 11.83 17.25 10.5 17.25C8.71 17.25 6.99 16.54 5.73 15.27C4.46 14.01 3.75 12.29 3.75 10.5Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'search-20': {
    viewBox: '0 0 20 20',
    body: (
      <>
        <path
          d="M17.94 17.06L14.03 13.15C15.16 11.79 15.73 10.04 15.61 8.27C15.49 6.51 14.69 4.85 13.38 3.66C12.07 2.46 10.36 1.82 8.58 1.86C6.81 1.9 5.13 2.62 3.87 3.87C2.62 5.13 1.9 6.81 1.86 8.59C1.82 10.36 2.46 12.07 3.66 13.38C4.85 14.69 6.51 15.49 8.27 15.61C10.04 15.73 11.79 15.16 13.15 14.03L17.06 17.94C17.12 18 17.18 18.05 17.26 18.08C17.34 18.11 17.42 18.13 17.5 18.13C17.58 18.13 17.66 18.11 17.74 18.08C17.82 18.05 17.88 18 17.94 17.94C18 17.88 18.05 17.82 18.08 17.74C18.11 17.66 18.13 17.58 18.13 17.5C18.13 17.42 18.11 17.34 18.08 17.26C18.05 17.18 18 17.12 17.94 17.06ZM3.13 8.75C3.13 7.64 3.45 6.55 4.07 5.63C4.69 4.7 5.57 3.98 6.6 3.55C7.63 3.13 8.76 3.02 9.85 3.23C10.94 3.45 11.94 3.99 12.73 4.77C13.51 5.56 14.05 6.56 14.27 7.65C14.48 8.74 14.37 9.87 13.95 10.9C13.52 11.93 12.8 12.81 11.88 13.43C10.95 14.05 9.86 14.38 8.75 14.38C7.26 14.37 5.83 13.78 4.77 12.73C3.72 11.67 3.13 10.24 3.13 8.75Z"
          fill="currentColor"
        />
      </>
    ),
  },
  shield: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M12 4.5V12H3.83C3.78 11.5 3.75 11 3.75 10.5V5.25C3.75 5.05 3.83 4.86 3.97 4.72C4.11 4.58 4.3 4.5 4.5 4.5H12ZM12 12V21.75C12 21.75 19.32 19.75 20.17 12H12Z"
          fill="currentColor"
        />
        <path
          d="M19.5 3.75H4.5C4.1 3.75 3.72 3.91 3.44 4.19C3.16 4.47 3 4.85 3 5.25V10.5C3 15.44 5.39 18.44 7.4 20.08C9.56 21.85 11.71 22.45 11.81 22.47C11.93 22.51 12.07 22.51 12.2 22.47C12.29 22.45 14.44 21.85 16.61 20.08C18.61 18.44 21 15.44 21 10.5V5.25C21 4.85 20.84 4.47 20.56 4.19C20.28 3.91 19.9 3.75 19.5 3.75ZM11.25 20.68C10.21 20.23 9.23 19.64 8.35 18.92C6.37 17.3 5.14 15.23 4.69 12.75H11.25V20.68ZM11.25 11.25H4.52C4.51 11 4.5 10.75 4.5 10.5V5.25H11.25V11.25ZM15.69 18.88C14.8 19.62 13.81 20.22 12.75 20.67V12.75H19.3C18.86 15.21 17.65 17.27 15.69 18.88ZM19.5 10.5C19.5 10.75 19.49 11 19.48 11.25H12.75V5.25H19.5V10.5Z"
          fill="currentColor"
        />
      </>
    ),
  },
  'shopping-cart': {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M3.75 6H20.1C20.21 6 20.32 6.02 20.42 6.07C20.52 6.12 20.61 6.19 20.68 6.27C20.75 6.35 20.8 6.45 20.83 6.56C20.85 6.67 20.86 6.78 20.84 6.88L19.61 13.63C19.58 13.81 19.49 13.96 19.35 14.08C19.22 14.19 19.05 14.25 18.87 14.25H5.25L3.75 6Z"
          fill="currentColor"
        />
        <path
          d="M18 17.25H6.42C6.25 17.25 6.08 17.19 5.94 17.08C5.81 16.96 5.71 16.81 5.68 16.63L3.18 2.87C3.15 2.69 3.06 2.54 2.92 2.42C2.79 2.31 2.62 2.25 2.44 2.25H0.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.75 21C7.79 21 8.63 20.16 8.63 19.13C8.63 18.09 7.79 17.25 6.75 17.25C5.71 17.25 4.88 18.09 4.88 19.13C4.88 20.16 5.71 21 6.75 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 21C19.04 21 19.88 20.16 19.88 19.13C19.88 18.09 19.04 17.25 18 17.25C16.96 17.25 16.13 18.09 16.13 19.13C16.13 20.16 16.96 21 18 21Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3.75 6H20.1C20.21 6 20.32 6.02 20.42 6.07C20.52 6.12 20.61 6.19 20.68 6.27C20.75 6.35 20.8 6.45 20.83 6.56C20.85 6.67 20.86 6.78 20.84 6.88L19.61 13.63C19.58 13.81 19.49 13.96 19.35 14.08C19.22 14.19 19.05 14.25 18.87 14.25H5.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  sliders: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M13.88 16.12L3.75 16.12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.25 16.12L17.63 16.12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15.75 18C16.79 18 17.63 17.16 17.63 16.12C17.63 15.09 16.79 14.25 15.75 14.25C14.71 14.25 13.88 15.09 13.88 16.12C13.88 17.16 14.71 18 15.75 18Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.88 7.87L3.75 7.87"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.25 7.87L11.63 7.87"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.75 9.75C10.79 9.75 11.63 8.91 11.63 7.87C11.63 6.84 10.79 6 9.75 6C8.71 6 7.88 6.84 7.88 7.87C7.88 8.91 8.71 9.75 9.75 9.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  star: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12.41 17.88L17.14 20.88C17.75 21.26 18.5 20.69 18.32 19.98L16.95 14.61C16.91 14.46 16.92 14.3 16.96 14.16C17.01 14.01 17.1 13.88 17.22 13.78L21.46 10.25C22.01 9.79 21.73 8.86 21.01 8.81L15.47 8.46C15.32 8.44 15.18 8.39 15.06 8.3C14.94 8.21 14.85 8.09 14.79 7.95L12.73 2.75C12.67 2.61 12.57 2.48 12.44 2.39C12.31 2.3 12.16 2.25 12 2.25C11.84 2.25 11.69 2.3 11.56 2.39C11.43 2.48 11.33 2.61 11.27 2.75L9.21 7.95C9.15 8.09 9.06 8.21 8.94 8.3C8.82 8.39 8.68 8.44 8.53 8.46L2.99 8.81C2.27 8.86 1.99 9.79 2.54 10.25L6.78 13.78C6.9 13.88 6.99 14.01 7.04 14.16C7.08 14.3 7.09 14.46 7.05 14.61L5.78 19.59C5.57 20.44 6.47 21.12 7.19 20.67L11.59 17.88C11.71 17.8 11.85 17.76 12 17.76C12.15 17.76 12.29 17.8 12.41 17.88Z"
          fill="currentColor"
        />
        <path
          d="M12.41 17.88L17.14 20.88C17.75 21.26 18.5 20.69 18.32 19.98L16.95 14.61C16.91 14.46 16.92 14.3 16.96 14.16C17.01 14.01 17.1 13.88 17.22 13.78L21.46 10.25C22.01 9.79 21.73 8.86 21.01 8.81L15.47 8.46C15.32 8.44 15.18 8.39 15.06 8.3C14.94 8.21 14.85 8.09 14.79 7.95L12.73 2.75C12.67 2.61 12.57 2.48 12.44 2.39C12.31 2.3 12.16 2.25 12 2.25C11.84 2.25 11.69 2.3 11.56 2.39C11.43 2.48 11.33 2.61 11.27 2.75L9.21 7.95C9.15 8.09 9.06 8.21 8.94 8.3C8.82 8.39 8.68 8.44 8.53 8.46L2.99 8.81C2.27 8.86 1.99 9.79 2.54 10.25L6.78 13.78C6.9 13.88 6.99 14.01 7.04 14.16C7.08 14.3 7.09 14.46 7.05 14.61L5.78 19.59C5.57 20.44 6.47 21.12 7.19 20.67L11.59 17.88C11.71 17.8 11.85 17.76 12 17.76C12.15 17.76 12.29 17.8 12.41 17.88V17.88Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  storefront: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M9 9V10.5C9 11.3 8.68 12.06 8.12 12.62C7.56 13.18 6.8 13.5 6 13.5C5.2 13.5 4.44 13.18 3.88 12.62C3.32 12.06 3 11.3 3 10.5V9"
          fill="currentColor"
        />
        <path
          opacity="0.2"
          d="M21 9V10.5C21 11.3 20.68 12.06 20.12 12.62C19.56 13.18 18.8 13.5 18 13.5C17.2 13.5 16.44 13.18 15.88 12.62C15.32 12.06 15 11.3 15 10.5V9"
          fill="currentColor"
        />
        <path
          d="M4.5 13.09V19.5C4.5 19.7 4.58 19.89 4.72 20.03C4.86 20.17 5.05 20.25 5.25 20.25H18.75C18.95 20.25 19.14 20.17 19.28 20.03C19.42 19.89 19.5 19.7 19.5 19.5V13.09"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5.07 3.75H18.93C19.1 3.75 19.26 3.8 19.39 3.9C19.52 4 19.61 4.14 19.66 4.29L21 9H3L4.34 4.29C4.39 4.14 4.48 4 4.61 3.9C4.74 3.8 4.9 3.75 5.07 3.75Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9 9V10.5C9 11.3 8.68 12.06 8.12 12.62C7.56 13.18 6.8 13.5 6 13.5C5.2 13.5 4.44 13.18 3.88 12.62C3.32 12.06 3 11.3 3 10.5V9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M15 9V10.5C15 11.3 14.68 12.06 14.12 12.62C13.56 13.18 12.8 13.5 12 13.5C11.2 13.5 10.44 13.18 9.88 12.62C9.32 12.06 9 11.3 9 10.5V9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M21 9V10.5C21 11.3 20.68 12.06 20.12 12.62C19.56 13.18 18.8 13.5 18 13.5C17.2 13.5 16.44 13.18 15.88 12.62C15.32 12.06 15 11.3 15 10.5V9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  truck: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.3"
          d="M16.5 13.5H1.5V17.25C1.5 17.45 1.58 17.64 1.72 17.78C1.86 17.92 2.05 18 2.25 18H4.13C4.13 17.4 4.36 16.83 4.78 16.41C5.21 15.99 5.78 15.75 6.38 15.75C6.97 15.75 7.54 15.99 7.97 16.41C8.39 16.83 8.63 17.4 8.63 18H15.38C15.37 17.61 15.48 17.22 15.68 16.87C15.87 16.53 16.16 16.25 16.5 16.05L16.5 13.5Z"
          fill="currentColor"
        />
        <path
          opacity="0.3"
          d="M19.88 18C19.88 17.61 19.77 17.22 19.57 16.88C19.38 16.53 19.09 16.25 18.75 16.05C18.41 15.85 18.02 15.75 17.63 15.75C17.23 15.75 16.84 15.85 16.5 16.05L16.5 11.25H22.5V17.25C22.5 17.45 22.42 17.64 22.28 17.78C22.14 17.92 21.95 18 21.75 18H19.88Z"
          fill="currentColor"
        />
        <path
          d="M22.5 11.25H16.5V7.5H20.49C20.64 7.5 20.79 7.54 20.91 7.63C21.04 7.71 21.13 7.83 21.19 7.97L22.5 11.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.5 13.5H16.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M17.63 20.25C18.87 20.25 19.88 19.24 19.88 18C19.88 16.76 18.87 15.75 17.63 15.75C16.38 15.75 15.38 16.76 15.38 18C15.38 19.24 16.38 20.25 17.63 20.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M6.38 20.25C7.62 20.25 8.63 19.24 8.63 18C8.63 16.76 7.62 15.75 6.38 15.75C5.13 15.75 4.13 16.76 4.13 18C4.13 19.24 5.13 20.25 6.38 20.25Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M15.38 18H8.63"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4.13 18H2.25C2.05 18 1.86 17.92 1.72 17.78C1.58 17.64 1.5 17.45 1.5 17.25V6.75C1.5 6.55 1.58 6.36 1.72 6.22C1.86 6.08 2.05 6 2.25 6H16.5V16.05"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.5 16.05V11.25H22.5V17.25C22.5 17.45 22.42 17.64 22.28 17.78C22.14 17.92 21.95 18 21.75 18H19.88"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  user: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          opacity="0.2"
          d="M12 15C15.31 15 18 12.31 18 9C18 5.69 15.31 3 12 3C8.69 3 6 5.69 6 9C6 12.31 8.69 15 12 15Z"
          fill="currentColor"
        />
        <path
          d="M12 15C15.31 15 18 12.31 18 9C18 5.69 15.31 3 12 3C8.69 3 6 5.69 6 9C6 12.31 8.69 15 12 15Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeMiterlimit="10"
        />
        <path
          d="M2.91 20.25C3.83 18.65 5.15 17.33 6.75 16.41C8.35 15.49 10.16 15 12 15C13.84 15 15.65 15.49 17.25 16.41C18.85 17.33 20.17 18.65 21.09 20.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  warning: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M22.19 17.62L13.95 3.37C13.75 3.03 13.47 2.75 13.12 2.55C12.78 2.35 12.39 2.25 12 2.25C11.61 2.25 11.22 2.35 10.88 2.55C10.53 2.75 10.25 3.03 10.05 3.37V3.37L1.81 17.62C1.61 17.96 1.5 18.35 1.5 18.75C1.5 19.14 1.61 19.53 1.8 19.87C2 20.22 2.28 20.5 2.63 20.7C2.97 20.89 3.36 21 3.75 21H20.25C20.64 21 21.03 20.89 21.37 20.7C21.72 20.5 22 20.22 22.2 19.87C22.39 19.53 22.5 19.14 22.5 18.75C22.5 18.35 22.39 17.96 22.19 17.62ZM11.25 9C11.25 8.8 11.33 8.61 11.47 8.47C11.61 8.33 11.8 8.25 12 8.25C12.2 8.25 12.39 8.33 12.53 8.47C12.67 8.61 12.75 8.8 12.75 9V13.5C12.75 13.7 12.67 13.89 12.53 14.03C12.39 14.17 12.2 14.25 12 14.25C11.8 14.25 11.61 14.17 11.47 14.03C11.33 13.89 11.25 13.7 11.25 13.5V9ZM12 18C11.78 18 11.56 17.93 11.38 17.81C11.19 17.69 11.05 17.51 10.96 17.31C10.88 17.1 10.85 16.87 10.9 16.66C10.94 16.44 11.05 16.24 11.2 16.08C11.36 15.92 11.56 15.82 11.78 15.77C12 15.73 12.23 15.75 12.43 15.84C12.64 15.92 12.81 16.07 12.94 16.25C13.06 16.43 13.13 16.65 13.13 16.88C13.13 17.17 13.01 17.46 12.8 17.67C12.58 17.88 12.3 18 12 18Z"
          fill="currentColor"
        />
      </>
    ),
  },
  x: {
    viewBox: '0 0 24 24',
    body: (
      <>
        <path
          d="M18.75 5.25L5.25 18.75"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18.75 18.75L5.25 5.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    ),
  },
  'x-14': {
    viewBox: '0 0 14 14',
    body: (
      <>
        <path
          d="M11.25 10.63C11.29 10.67 11.32 10.72 11.34 10.77C11.36 10.82 11.38 10.88 11.38 10.94C11.38 11 11.36 11.05 11.34 11.11C11.32 11.16 11.29 11.21 11.25 11.25C11.21 11.29 11.16 11.32 11.11 11.34C11.05 11.36 11 11.38 10.94 11.38C10.88 11.38 10.82 11.36 10.77 11.34C10.72 11.32 10.67 11.29 10.63 11.25L7 7.62L3.37 11.25C3.29 11.33 3.18 11.38 3.06 11.38C2.95 11.38 2.84 11.33 2.75 11.25C2.67 11.16 2.62 11.05 2.62 10.94C2.62 10.82 2.67 10.71 2.75 10.63L6.38 7L2.75 3.37C2.67 3.29 2.62 3.18 2.62 3.06C2.62 2.95 2.67 2.84 2.75 2.75C2.84 2.67 2.95 2.62 3.06 2.62C3.18 2.62 3.29 2.67 3.37 2.75L7 6.38L10.63 2.75C10.71 2.67 10.82 2.62 10.94 2.62C11.05 2.62 11.16 2.67 11.25 2.75C11.33 2.84 11.38 2.95 11.38 3.06C11.38 3.18 11.33 3.29 11.25 3.37L7.62 7L11.25 10.63Z"
          fill="currentColor"
        />
      </>
    ),
  },
} satisfies Record<string, { viewBox: string; body: ReactNode }>;

export type IconName = keyof typeof ICONS;

type IconProps = Omit<SVGProps<SVGSVGElement>, 'name'> & {
  name: IconName;
  /** Rendered size in px; defaults to the Figma frame size of the icon (24, 20, 18 or 14). */
  size?: number;
  /** Accessible name; without it the icon is decorative (`aria-hidden`). */
  title?: string;
};

export function Icon({ name, size, title, className, ...props }: IconProps) {
  const icon = ICONS[name];
  const frame = Number(icon.viewBox.split(' ')[2]);

  return (
    <svg
      viewBox={icon.viewBox}
      width={size ?? frame}
      height={size ?? frame}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={cn('shrink-0', className)}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {icon.body}
    </svg>
  );
}
